<template>
  <gg-card>
    <gg-card-header>
      Upload Your Logo &amp; Other Featured Images
    </gg-card-header>
    <gg-card-text>
      <v-row>
        <v-col cols="12">
          <p class="mb-0">
            Browse to select your Logo, and/or any other images that you wish to feature on your website. If you don't have a Logo or other images right now, click <i>Next Step</i>.
          </p>
        </v-col>
        <v-col cols="12">
          <v-file-input
            v-model="file"
            show-size
            :accept="allowed_files"
            outlined
            placeholder="Select an image for your logo"
            hint="Preferred sizes: Vertical: 160px * 160px Horizontal: 350px * 75px"
            persistent-hint
            small-chips
            dense
            prepend-icon="mdi-paperclip"
            :loading="loadingLogo"
            :disabled="!!logoFile"
            @change="uploadLogo"
          />
        </v-col>
        <v-col cols="12" v-if="logoFile">
          <v-chip-group>
            <v-chip close @click:close="removeFile">
              {{ file.name }}
            </v-chip>
          </v-chip-group>
        </v-col>
        <v-col cols="12">
          <v-file-input
            v-model="otherImages"
            multiple
            :clearable="false"
            show-size
            :accept="allowed_files"
            outlined
            placeholder="Select Other Images"
            :hint="`You can upload up to ${maxImgsCount} images`"
            persistent-hint
            small-chips
            dense
            prepend-icon="mdi-paperclip"
            :disabled="otherDisabled"
            :loading="loadingOther"
            @change="uploadOther"
          />
        </v-col>
        <v-col cols="12" v-if="otherImgs.length">
          <v-chip-group>
            <v-chip
              v-for="(file, i) in otherImgs"
              :key="`o-img-${i}`"
              close
              @click:close="removeOther(file)"
            >
              {{ file.name }}
            </v-chip>
          </v-chip-group>
        </v-col>
      </v-row>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import {
  readUploadedFile,
  checkMimeType,
  checkFileSize,
  uploadFile,
  deleteFile
} from '@/helpers/file-upload'

const maxFiles = 5

export default {
  name: 'WebsiteLogo',
  data () {
    return {
      loadingLogo: false,
      allowed_files: [
        'image/webp',
        'image/gif',
        'image/vnd.microsoft.icon',
        'image/jpeg',
        'image/png',
        'image/svg+xml',
        'text/plain',
      ],
      allowed_size: 2, // in mb
      file: [],
      otherImages: [],
      loadingOther: false,
    }
  },
  computed: {
    ...mapGetters({
      starterSite: 'wizard_model/starterSite'
    }),
    logoFile: {
      get () {
        return this.starterSite.logoFile
      },
      set (val) {
        this.SET_SS_LOGO_FILE(val)
      }
    },
    otherImgs: {
      get () {
        return this.starterSite.other_images
      },
      set (val) {
        this.SET_SS_OTHER_IMGS(val)
      }
    },
    otherDisabled () {
      return this.otherImgs.length >= maxFiles
    },
    maxImgsCount () {
      return maxFiles - this.otherImgs.length
    },
  },
  methods: {
    ...mapActions({
      reportError: 'reportError'
    }),
    ...mapMutations({
      SET_SS_LOGO_FILE: 'wizard_model/SET_SS_LOGO_FILE',
      SET_SS_OTHER_IMGS: 'wizard_model/SET_SS_OTHER_IMGS'
    }),
    async uploadLogo () {
      if (!checkMimeType(this.file, this.allowed_files)) {
        return
      }
      if (!checkFileSize(this.file, this.allowed_size)) {
        return
      }
      this.loadingLogo = true
      const file = await readUploadedFile(this.file)
      try {
        const id = await uploadFile(file)
        this.logoFile = {
          id
        }
      } catch (e) {
        console.warn(e)
      } finally {
        this.loadingLogo = false
      }
    },
    async uploadOther () {
      if ((this.otherImages.length + this.otherImgs.length) > maxFiles) return
      // check filesize
      let validFilesize = true
      this.otherImages.forEach(file => {
        if (validFilesize) {
          validFilesize = checkFileSize(file, this.allowed_size)
        }
      })
      if (!validFilesize) return
      // upload files
      this.loadingOther = true
      try {
        const filesToClear = []
        for await (const file of this.otherImages) {
          const fileReader = await readUploadedFile(file)
          const id = await uploadFile(fileReader)
          this.otherImgs = [...this.otherImgs, { name: file.name, id }]
          const fileIndex = this.otherImages.findIndex(fl => {
            return fl.name === file.name && fl.size === file.size
          })
          filesToClear.push(fileIndex)
        }
        // remove chip from file-input component
        if (filesToClear.length) {
          const newOtherImgs = this.otherImages.filter((f, i) => filesToClear.indexOf(i) === -1)
          this.otherImages.splice(0, this.otherImages.length, ...newOtherImgs)
        }
      } catch (e) {
        this.reportError('Uploading error')
        this.otherImgs = []
        this.otherImages = []
      } finally {
        this.loadingOther = false
      }
    },
    async removeFile () {
      try {
        await deleteFile(this.logoFile.id)
        this.file = []
        this.logoFile = ''
      } catch (e) {
        this.reportError(e)
      }
    },
    async removeOther (file) {
      try {
        await deleteFile(file.id)
        // remove file from store
        this.otherImgs = this.otherImgs.filter(img => img.id !== file.id)
      } catch (e) {
        this.reportError(e)
      }
    },
    validate () {
      return true
    }
  },
}
</script>

<style scoped>

</style>
